/**
 * BulkDeleteEmployeesDialog — three-step wizard for mass deletion:
 *   1) Upload XLSX with MATRICULA + NOME + CPF columns
 *   2) Review matched employees + not-found rows
 *   3) Confirm and execute (irreversible)
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import {
  Upload, Loader2, FileSpreadsheet, AlertTriangle, X, Trash2,
  ShieldAlert, CheckCircle2,
} from "lucide-react";
import { API, useAuth } from "../App";

export default function BulkDeleteEmployeesDialog({
  open, onClose, onDeleted,
}) {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setFile(null); setPreview(null);
      setConfirmChecked(false); setDeleting(false);
    }
  }, [open]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xlsx?|XLSX?)$/.test(f.name)) {
      toast.error("Formato inválido. Envie XLSX ou XLS.");
      return;
    }
    setFile(f); setPreview(null); setConfirmChecked(false);
  };

  const runPreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(
        `${API}/admin/employees/bulk-delete/preview`, fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          }, timeout: 60000,
        },
      );
      setPreview(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao ler planilha");
    } finally { setLoading(false); }
  };

  const executeDelete = async () => {
    if (!preview?.matched?.length) return;
    setDeleting(true);
    try {
      const r = await axios.post(
        `${API}/admin/employees/bulk-delete/execute`,
        { employee_ids: preview.matched.map((m) => m.id) },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 },
      );
      toast.success(
        `${r.data.deleted_employees} funcionário(s) removido(s). ` +
        `${r.data.deleted_payslips} contracheque(s) apagado(s).`,
      );
      onDeleted?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao excluir");
    } finally { setDeleting(false); }
  };

  const reset = () => {
    setFile(null); setPreview(null); setConfirmChecked(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="sm:max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid="bulk-delete-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-red-700" />
            Lista de Exclusão em Massa
          </DialogTitle>
          <DialogDescription>
            Envie uma planilha XLSX com colunas <strong>MATRÍCULA</strong>,{" "}
            <strong>NOME</strong> e <strong>CPF</strong>. O sistema mostra
            quem foi localizado antes de excluir. Match por matrícula &gt;
            CPF &gt; nome. Ação irreversível.
          </DialogDescription>
        </DialogHeader>

        {/* STEP 1 - upload zone */}
        {!preview && (
          <>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                file
                  ? "border-red-300 bg-red-50/50"
                  : "border-slate-300 bg-slate-50 hover:border-red-300"
              }`}
            >
              {file ? (
                <div className="space-y-2">
                  <FileSpreadsheet className="w-10 h-10 text-red-600 mx-auto" />
                  <div className="font-medium text-sm text-slate-900">{file.name}</div>
                  <div className="text-xs text-slate-500">
                    {(file.size / 1024).toFixed(1)} KB
                  </div>
                  <Button variant="ghost" size="sm" onClick={reset}
                    className="text-slate-500" data-testid="bulkdel-clear">
                    <X className="w-3 h-3 mr-1" /> Trocar arquivo
                  </Button>
                </div>
              ) : (
                <label className="cursor-pointer block">
                  <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                  <div className="text-sm text-slate-700 font-medium">
                    Clique para selecionar a planilha
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    .xlsx ou .xls · colunas: MATRÍCULA, NOME, CPF
                  </div>
                  <input ref={inputRef} type="file" className="hidden"
                    accept=".xlsx,.xls" onChange={onPick}
                    data-testid="bulkdel-file-input" />
                </label>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose}
                data-testid="bulkdel-cancel-step1">
                Cancelar
              </Button>
              <Button
                onClick={runPreview}
                disabled={!file || loading}
                className="bg-slate-800 hover:bg-slate-900 text-white"
                data-testid="bulkdel-preview-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analisando…
                  </>
                ) : (
                  <>Analisar planilha →</>
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* STEP 2 - preview + confirm */}
        {preview && (
          <>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="border border-red-200 bg-red-50 rounded-md p-2">
                <div className="text-2xl font-bold text-red-800"
                  data-testid="bulkdel-matched-count">
                  {preview.matched_count}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-red-700">
                  Serão excluídos
                </div>
              </div>
              <div className="border border-amber-200 bg-amber-50 rounded-md p-2">
                <div className="text-2xl font-bold text-amber-800">
                  {preview.not_found_count}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-amber-700">
                  Não localizados
                </div>
              </div>
              <div className="border border-slate-200 bg-slate-50 rounded-md p-2">
                <div className="text-2xl font-bold text-slate-800">
                  {preview.payslips_count}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-slate-600">
                  Contracheques que serão apagados
                </div>
              </div>
            </div>

            {/* Matched table */}
            {preview.matched_count > 0 && (
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <div className="bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-800 flex items-center gap-1.5">
                  <Trash2 className="w-3.5 h-3.5" />
                  Funcionários que serão excluídos
                </div>
                <div className="max-h-56 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-[10px] uppercase h-7">Matrícula</TableHead>
                        <TableHead className="text-[10px] uppercase h-7">Nome</TableHead>
                        <TableHead className="text-[10px] uppercase h-7">CPF</TableHead>
                        <TableHead className="text-[10px] uppercase h-7">Match por</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.matched.slice(0, 500).map((m) => (
                        <TableRow key={m.id}>
                          <TableCell className="font-mono text-xs py-1">{m.matricula}</TableCell>
                          <TableCell className="text-xs py-1">{m.nome}</TableCell>
                          <TableCell className="font-mono text-xs py-1">{m.cpf_masked}</TableCell>
                          <TableCell className="text-[10px] py-1 text-slate-500">{m.match_method}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Not found list */}
            {preview.not_found?.length > 0 && (
              <div className="border border-amber-200 rounded-md overflow-hidden">
                <div className="bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Não localizados ({preview.not_found_count})
                </div>
                <div className="max-h-40 overflow-y-auto divide-y divide-slate-100 text-xs">
                  {preview.not_found.map((n, i) => (
                    <div key={i} className="px-3 py-1 flex items-center gap-3">
                      <span className="font-mono text-slate-600">{n.matricula}</span>
                      <span className="text-slate-800 flex-1">{n.nome}</span>
                      <span className="font-mono text-slate-500">{n.cpf}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Confirmation */}
            {preview.matched_count > 0 ? (
              <div className="border-2 border-red-200 bg-red-50/50 rounded-md p-3">
                <label className="flex items-start gap-2 text-xs text-red-900 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirmChecked}
                    onChange={(e) => setConfirmChecked(e.target.checked)}
                    className="mt-0.5"
                    data-testid="bulkdel-confirm-check"
                  />
                  <span>
                    Entendo que <strong>{preview.matched_count} funcionário(s)</strong>{" "}
                    e <strong>{preview.payslips_count} contracheque(s)</strong> serão
                    excluídos permanentemente do sistema. Esta ação não pode ser desfeita.
                  </span>
                </label>
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500 text-sm">
                <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
                Nenhum funcionário localizado nessa planilha. Nada a excluir.
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={reset}
                data-testid="bulkdel-back-btn">
                Voltar
              </Button>
              <Button variant="outline" onClick={onClose} disabled={deleting}
                data-testid="bulkdel-cancel-step2">
                Cancelar
              </Button>
              {preview.matched_count > 0 && (
                <Button
                  onClick={executeDelete}
                  disabled={!confirmChecked || deleting}
                  className="bg-red-700 hover:bg-red-800 text-white"
                  data-testid="bulkdel-execute-btn"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Excluindo…
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Excluir {preview.matched_count} funcionário(s)
                    </>
                  )}
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
